// fix-produce: what a paid tap says when the server says no. A confirm sheet ("Finish it all?", "Animate scene 2?")
// never just closes on a failure: a new price (CONFIRM_REQUIRED, the plan costs more than the yes) is shown in the
// sheet and asked again; anything else is a plain sentence the sheet shows, and the sheet stays open.
import { ApiError, normProducePrice } from "./api";
import type { Estimate } from "./types";

export type PaidFailure =
  /** it costs more now: show this price and ask again */
  | { kind: "reprice"; price: Estimate; text: string }
  | { kind: "error"; text: string; code: string | null };

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

const lower = (s: string) => (s ? s[0]!.toLowerCase() + s.slice(1) : s);
const joinAnd = (xs: string[]) => (xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);

/** "It's now 19 credits: music, voiceover and final edit." */
export function repriceText(price: Estimate): string {
  const what = joinAnd(price.breakdown.filter((l) => l.credits > 0).map((l) => lower(l.label)));
  return `It's now ${price.credits} credits${what ? `: ${what}` : ""}.`;
}

/** The new price from a CONFIRM_REQUIRED refusal: a produce plan (details.plan) or a single clip (details.credits). */
export function newPrice(details: unknown): Estimate | null {
  if (!isObj(details)) return null;
  if (isObj(details.plan)) return normProducePrice(details.plan);
  if (typeof details.credits === "number") return { credits: details.credits, usd: 0, breakdown: [] };
  return null;
}

/** A plain sentence for any failure of a paid tap (also used by the toasts of taps that have no sheet). */
export function failureText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 0 && e.code === "CANCELLED") return "Cancelled.";
    if (e.status === 402) return /credit/i.test(e.message) ? e.message : "Not enough credits for that. Top up, then try again.";
    if (e.status >= 500) return "Something went wrong on our side. Try again in a moment.";
    return e.message || "That didn't work.";
  }
  // fetch itself failed: offline, or the server can't be reached
  if (e instanceof TypeError) return "Couldn't reach the studio. Check your connection and try again.";
  return (e as Error)?.message || "That didn't work.";
}

/** What a confirm sheet does with a failed yes. */
export function paidFailure(e: unknown): PaidFailure {
  if (e instanceof ApiError && e.code === "CONFIRM_REQUIRED") {
    const price = newPrice(e.details);
    if (price && price.credits > 0) return { kind: "reprice", price, text: repriceText(price) };
  }
  return { kind: "error", text: failureText(e), code: e instanceof ApiError ? e.code : null };
}
